import { flow } from 'fp-ts/function';
import { forEach, get, head, values } from 'lodash/fp';

import { FeatureFlag, TextKey } from '@mews-distributor/common';
import { keyById } from '@mews/fp';
import { BadRequestError, BusinessError, createAction, NotAuthorizedError, QuotaExceededError } from '@mews/framework';
import { API_MAXIMUM_INTERVAL_TIME_UNITS } from '../../../constants';
import { getMinimalStartDate } from '../../../selectors/views/dates';
import {
    getSelectedCityDayUseEnterprises,
    getSelectedCityEnterprises,
} from '../../../selectors/views/selectedCityEnterprises';
import { logErrorKey } from '../../../services/error/actions';
import { trackEvent } from '../../../services/logging/loggingHelpers';
import { DistributorPromiseThunkAction } from '../../../types';
import { WithCurrencyCode } from '../../../types/selectors/selectorPropTypes';
import { toDateTimeUtc } from '../../../utils/dates';
import { getAvailabilityHash } from '../../../utils/getAvailabilityHash';
import { isIntervalLimitExceeded } from '../../../utils/intervalLimit';
import { getCurrentAlternativeSuggestions } from '../../../views/spaceCategories/selectors';
import { getCurrencyCode } from '../../app/selectors';
import { isMultiHotel } from '../../configuration/selectors';
import { hasSelectedCityPropertyAvailableRooms } from '../../hotels/selectors';
import { OTHER_DATES_SUGGESTED } from '../../otherDatesSuggestions/actionTypes';
import { fetchProducts } from '../../products/actions';
import { DEFAULT_RESOURCE_ORDER_SET } from '../../resourceOrder/actionTypes';
import {
    getEndDate,
    getEndTimeUtc,
    getStartDate,
    getStartTimeUtc,
    getVoucherCode,
} from '../../resourceOrder/selectors';
import { fetchHourlyServiceAvailability, fetchServiceAvailability } from '../../serviceAvailability';
import { Enterprise } from '../enterprises/types';
import { fetchExchangeRatesWithCheck, fetchFailedExchangeRates } from '../exchangeRates/actions';
import { getSelectedEnterpriseBasicProductIds } from '../products/selectors';
import { formatViolatedRestrictions, getViolatedDateMetrics } from '../restrictions/violatedRestrictions';
import { SERVICE_DATA_LOADED } from './actionTypes';
import { fetchEnterprisePricing } from './enterpriseServicePricing';
import { getEnterpriseServiceId, getServiceTimeUnit, isServiceHourly } from './selectors';

interface FetchServiceOptions {
    startDate: string;
    endDate: string;
    enterprise: Enterprise;
    currencyCode: string;
    voucherCode: string | undefined;
    startTimeUtc?: string;
    endTimeUtc?: string;
}

const fetchService =
    ({
        startDate,
        endDate,
        enterprise,
        currencyCode,
        voucherCode,
        startTimeUtc,
        endTimeUtc,
    }: FetchServiceOptions): DistributorPromiseThunkAction =>
    async (dispatch, getState, { appClockService, featureFlagService }) => {
        const state = getState();
        const nowUtc = appClockService.getAppNowUtc();

        const startUtc = toDateTimeUtc(startDate, enterprise.ianaTimeZoneIdentifier);
        const endUtc = toDateTimeUtc(endDate, enterprise.ianaTimeZoneIdentifier);

        const enterpriseId = enterprise.id;
        const isBookingEngineForHourlyServiceEnabled = featureFlagService.hasFlag(
            FeatureFlag.bookingEngineForHourlyServiceEnabled
        );
        const isHourlyService = isServiceHourly(state, { enterpriseId });
        const isHourlyServiceEnabled = isBookingEngineForHourlyServiceEnabled && isHourlyService;

        try {
            const serviceId = getEnterpriseServiceId(state, { enterpriseId });
            const timeUnit = getServiceTimeUnit(state, { enterpriseId });

            if (isIntervalLimitExceeded(startDate, endDate, timeUnit)) {
                const error = new QuotaExceededError(
                    `The interval must not exceed ${API_MAXIMUM_INTERVAL_TIME_UNITS} timeUnits. startDate: ${startDate}, endDate: ${endDate}`
                );
                dispatch(logErrorKey(TextKey.DateIntervalExceededErrorMessage));
                throw error;
            }
            const fetchAvailabilityFunc = isHourlyServiceEnabled
                ? fetchHourlyServiceAvailability
                : fetchServiceAvailability;
            const promises = [
                ...(serviceId != null ? [dispatch(fetchProducts(serviceId))] : []),
                dispatch(fetchAvailabilityFunc({ endUtc, enterpriseId, startUtc })),
                dispatch(fetchExchangeRatesWithCheck({ sourceCurrencyCode: currencyCode, enterpriseId })),
            ];
            await Promise.all(promises);

            const servicePricingResponse = await dispatch(
                fetchEnterprisePricing({
                    currencyCode,
                    endDate,
                    enterprise,
                    startDate,
                    voucherCode,
                    startTimeUtc,
                    endTimeUtc,
                })
            );
            if (servicePricingResponse == null) {
                return;
            }

            const formattedRestrictionsData = keyById(
                servicePricingResponse.violatedRestrictions.map((restriction) =>
                    formatViolatedRestrictions(
                        restriction,
                        getViolatedDateMetrics({
                            nowUtc,
                            startDateString: startDate,
                            endDateString: endDate,
                            timezone: enterprise.ianaTimeZoneIdentifier,
                        })
                    )
                )
            );

            dispatch(trackEvent('RestrictionsViolated', { violatedRestrictions: formattedRestrictionsData }));
            dispatch(createAction(SERVICE_DATA_LOADED));
        } catch (error) {
            if (
                (error instanceof BusinessError || error instanceof BadRequestError) &&
                !(error instanceof NotAuthorizedError)
            ) {
                return;
            }
            throw error;
        }
    };

interface FetchMultipleServicesOptions {
    startDate: string;
    endDate: string;
    enterprises: Enterprise[];
    currencyCode: string;
    voucherCode: string | undefined;
    startTimeUtc?: string;
    endTimeUtc?: string;
}

const fetchMultipleServices =
    ({
        startDate,
        endDate,
        enterprises,
        currencyCode,
        voucherCode,
        startTimeUtc,
        endTimeUtc,
    }: FetchMultipleServicesOptions): DistributorPromiseThunkAction =>
    async (dispatch, getState) => {
        const promises = enterprises.map(async (enterprise) =>
            dispatch(
                fetchService({ startDate, endDate, enterprise, currencyCode, voucherCode, startTimeUtc, endTimeUtc })
            )
        );

        await Promise.all(promises);

        const productIds = getSelectedEnterpriseBasicProductIds(getState());
        dispatch(createAction(DEFAULT_RESOURCE_ORDER_SET, { productIds }));
    };

export function fetchCurrentDatesAvailability({ currencyCode }: WithCurrencyCode): DistributorPromiseThunkAction {
    return async (dispatch, getState, { appClockService, loggerService }) => {
        const state = getState();
        const startDate = getStartDate(state);
        const endDate = getEndDate(state);
        const startTimeUtc = getStartTimeUtc(state);
        const endTimeUtc = getEndTimeUtc(state);
        const hotels =
            startDate === endDate ? getSelectedCityDayUseEnterprises(state, {}) : getSelectedCityEnterprises(state, {});
        const voucherCode = getVoucherCode(state);

        if (startDate == null || endDate == null) {
            throw new TypeError('startDate and endDate must be defined');
        }

        const enterprises = values(hotels);

        return dispatch(
            fetchMultipleServices({
                startDate,
                endDate,
                enterprises,
                voucherCode,
                currencyCode,
                startTimeUtc,
                endTimeUtc,
            })
        )
            .then(() => {
                // getState needs to be called again because fetchAvailability updated the store and we need the new data
                const newState = getState();
                // other dates suggestions, only in single hotel mode
                if (!isMultiHotel(newState) && !hasSelectedCityPropertyAvailableRooms(newState)) {
                    const nowUtc = appClockService.getAppNowUtc();
                    const todayDate = getMinimalStartDate(newState, { nowUtc });
                    const enterpriseId = getFirstEnterpriseId(enterprises);

                    const availabilityHash = getAvailabilityHash({
                        startDate: startTimeUtc ?? startDate,
                        endDate: endTimeUtc ?? endDate,
                        enterpriseId,
                        voucherCode,
                    });
                    const suggestions = getCurrentAlternativeSuggestions(newState, {
                        startDate,
                        endDate,
                        todayDate,
                        enterpriseId,
                    });

                    dispatch(
                        createAction(OTHER_DATES_SUGGESTED, {
                            startDate: startTimeUtc ?? startDate,
                            endDate: endTimeUtc ?? endDate,
                            enterpriseId,
                            voucherCode,
                            suggestions,
                            availabilityHash,
                        })
                    );
                    forEach((dates) => {
                        void dispatch(
                            fetchMultipleServices({
                                startDate: dates.startDate,
                                endDate: dates.endDate,
                                enterprises,
                                voucherCode,
                                currencyCode,
                            })
                        );
                    }, suggestions);
                }
            })
            .catch((error) => {
                if (error instanceof QuotaExceededError) {
                    return loggerService.log(
                        'fetch-availability-interval-exceeded',
                        `The interval must not exceed ${API_MAXIMUM_INTERVAL_TIME_UNITS} timeUnits.`,
                        {
                            error,
                        }
                    );
                }
                throw error;
            });
    };
}

const getFirstEnterpriseId = flow(head, get('id'));

export function fetchCurrentDatesAvailabilityWithSelectedCurrencyCode(): DistributorPromiseThunkAction {
    return async (dispatch, getState) => {
        const currencyCode = getCurrencyCode(getState());
        if (currencyCode == null) {
            throw new TypeError('Currency code is not defined');
        }
        await Promise.all([
            dispatch(fetchFailedExchangeRates()),
            dispatch(fetchCurrentDatesAvailability({ currencyCode })),
        ]);
    };
}
